<template>
  <CollaborativeEditable
    :input-name="resizeVerticalId"
    class="resize-vertical"
  >
    <div
      class="resize-vertical__handle"
      :tabindex="isDisabled ? undefined : 0"
      :arial-label="$t('Drag to resize window or use arrow keys')"
      :aria-disabled="isDisabled"
      :class="{
        'resize-vertical__handle--resizing': isResizing,
        'resize-vertical__handle--disabled': isDisabled,
      }"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @touchmove="onTouchMove"
      @keydown.up="$emit('resize-down')"
      @keydown.down="$emit('resize-up')"
      @focus="onFocus"
      @blur="onBlur"
    >
      <NebulaIcon
        :size="size"
        symbol-id="move-vertical--filled"
        class="resize-vertical__icon"
      />
    </div>
  </CollaborativeEditable>
</template>

<script>
import { first } from 'lodash-es';
import { mapState, mapActions } from 'pinia';
import { NebulaIcon } from '@discoveryedu/nebula-components';
import CollaborativeEditable from '@/components/CollaborativeEditable.vue';
import * as types from '@/lib/constants/store';
import {
  useEditorStore,
} from '@/stores';

export default {
  name: 'ResizeVertical',
  components: {
    CollaborativeEditable,
    NebulaIcon,
  },
  emits: [
    'resize-start',
    'resize-move',
    'resize-end',
    'resize-up',
    'resize-down',
    'focus',
    'blur',
  ],
  props: {
    id: {
      type: String,
      required: true,
    },
    size: {
      type: String,
      default: 'm',
    },
  },
  data() {
    return {
      resizeData: null,
      isFocused: false,
    };
  },
  beforeUnmount() {
    this.removeResizeHandlers();
  },
  watch: {
    isFocused() {
      this.setEditing(this.isFocused);
      this.$emit(this.isFocused ? 'focus' : 'blur');
    },
    isDisabled() {
      if (this.isDisabled) {
        // Finish resizing if locked out by another user.
        if (this.isResizing) {
          this.onResizeEnd();
        }
      }
    },
  },
  computed: {
    ...mapState(useEditorStore, {
      connectedUser: 'currentUserConnection',
    }),
    ...mapState(useEditorStore, [
      'isLocked',
    ]),
    isDisabled() {
      return this.isLocked(this.resizeVerticalId);
    },
    resizeVerticalId() {
      return `resize-vertical-${this.id}`;
    },
    isResizing() {
      return !!this.resizeData;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_CURRENTLY_EDITING,
    ]),
    attachResizeHandlers() {
      document.addEventListener('mousemove', this.onResize);
      document.addEventListener('mouseup', this.onResizeEnd);
      document.addEventListener('touchmove', this.onResize);
      document.addEventListener('touchend', this.onResizeEnd);
    },
    removeResizeHandlers() {
      document.removeEventListener('mousemove', this.onResize);
      document.removeEventListener('mouseup', this.onResizeEnd);
      document.removeEventListener('touchmove', this.onResize);
      document.removeEventListener('touchend', this.onResizeEnd);
    },
    onFocus() {
      this.isFocused = true;
    },
    onBlur() {
      this.isFocused = false;
    },
    setEditing(isEditing) {
      /*
        If we are starting edit mode, lock the component.
        If we are exiting edit mode, then only release the lock
        if the component is not already locked by another user.
      */
      const shouldUpdateEditing = isEditing || !this.isDisabled;
      if (shouldUpdateEditing) {
        this[types.UPDATE_CURRENTLY_EDITING]({
          [this.resizeVerticalId]: isEditing ? this.connectedUser?.id : null,
        });
      }
    },
    getEventPosition(event) {
      const clientX = first(event.targetTouches)?.clientX || event.clientX;
      const clientY = first(event.targetTouches)?.clientY || event.clientY;
      return { clientX, clientY };
    },
    onResizeStart(event) {
      if (this.isDisabled) return;
      this.attachResizeHandlers();
      const { clientX, clientY } = this.getEventPosition(event);
      this.resizeData = {
        startX: clientX,
        startY: clientY,
        moveY: clientY,
        moveX: clientX,
      };
      this.$emit('resize-start', this.resizeData);
    },
    onResize(event) {
      if (!this.isResizing) return;
      const { clientX, clientY } = this.getEventPosition(event);
      this.resizeData.moveX = clientX;
      this.resizeData.moveY = clientY;
      this.$emit('resize-move', this.resizeData);
    },
    onResizeEnd() {
      if (!this.isResizing) return;
      this.removeResizeHandlers();
      this.$emit('resize-end', this.resizeData);
      this.resizeData = null;
    },
    onTouchMove(event) {
      /*
        Prevent default behavior that would normally
        drag the entire UI. We only want the handle to move.
      */
      return event.preventDefault();
    },
  },
};
</script>

<style lang="stylus">
.resize-vertical {
  border-radius: 50%;

  &__handle {
    background-color: white;
    border-radius: 50%;
    box-shadow: $nebula-shadow-200;
    cursor: grab;
    display: flex;
    padding: $nebula-space-half;

    &:not(.resize-vertical__handle--resizing) {
      &:not(.resize-vertical__handle--disabled) {
        &:hover,
        &:focus {
          background-color: $nebula-color-platform-interactive-200;
        }
      }
    }

    &--resizing {
      background-color: $nebula-color-platform-interactive-400;
      cursor: grabbing;
    }

    &--disabled {
      .resize-vertical__icon {
        opacity: 0.5;
      }
    }
  }
}
</style>
